import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { fetchReps, updateRep, getBearer } from '../../api';
import { Select, SelectTrigger, SelectContent, SelectItem } from '../Select';
import { Text } from '../Text';

const definedTranslation = {
  assignedRepSelect: {
    assignedRepLabel: {
      id: 'assigned-rep-label',
      defaultMessage: 'Assigned Rep',
    },
    noAvailableReps: {
      id: 'no-available-reps',
      defaultMessage: 'No available reps',
    },
    changeRepLabel: {
      id: 'change-rep-label',
      defaultMessage: 'Change rep',
    },
  },
};

export interface RepSelectOptions {
  value: string;
  label: string;
  image: string | null;
  firstName: string;
  lastName: string;
}

export const AssignedRepSelect = ({
  onAssignedRepChange,
  onRepsLoaded,
  saveRepOnChange = true,
  autoselectFirstRep = true,
  productEntityId,
}: {
  onAssignedRepChange: (repId: string) => void;
  onRepsLoaded?: (reps: RepSelectOptions[]) => void;
  saveRepOnChange?: boolean;
  autoselectFirstRep?: boolean;
  productEntityId?: string;
}) => {
  const intl = useIntl();
  const [currentRepId, setCurrentRepId] = useState('');
  const [reps, setReps] = useState<RepSelectOptions[]>([]);
  const [isLoadingReps, setIsLoadingReps] = useState(false);

  const matchingRep = useMemo(() => {
    return reps?.find((rep) => rep.value === currentRepId);
  }, [reps, currentRepId]);

  useEffect(() => {
    onAssignedRepChange(currentRepId);
  }, [currentRepId]);

  const loadAssignedReps = () => {
    const bearerToken = getBearer();
    const productId =
      productEntityId || window.productAttributeJson?.product_id?.value;

    setIsLoadingReps(true);
    fetchReps(bearerToken, productId)
      .then((res) => {
        const assignableReps = res?.assignable_reps ?? [];
        const assignedRep = res?.assigned_rep ? [res.assigned_rep] : [];
        // The assigned rep doesn't show up in the assignable reps list, so we need to add it separately
        const allReps = [...assignableReps, ...assignedRep];

        const repsAsSelectOptions = allReps.map((rep) => {
          return {
            value: rep.id,
            label: `${rep.first_name} ${rep.last_name}`,
            image: rep.image,
            firstName: rep.first_name,
            lastName: rep.last_name,
          };
        });
        setReps(repsAsSelectOptions);

        if (res?.assigned_rep?.id) {
          setCurrentRepId(res.assigned_rep.id);
        } else if (autoselectFirstRep) {
          // Only autoselect the first rep if there is no rep already assigned
          setCurrentRepId(repsAsSelectOptions?.[0]?.value);
        }
        onRepsLoaded && onRepsLoaded(repsAsSelectOptions);
      })
      .finally(() => {
        setIsLoadingReps(false);
      });
  };

  const onRepChange = (repId: string) => {
    setCurrentRepId(repId);
    if (saveRepOnChange) {
      updateRep(repId);
    }
  };

  useEffect(() => {
    loadAssignedReps();
  }, []);

  const repImage = useMemo(() => {
    if (matchingRep?.image) {
      return (
        <img
          src={`${MEDIA_URL}${matchingRep.image}`}
          className="w-9 h-9 rounded-full"
          alt={intl?.formatMessage(
            definedTranslation.assignedRepSelect.assignedRepLabel,
          )}
        />
      );
    }

    const firstInitial = matchingRep?.firstName?.charAt(0) ?? '';
    const lastInitial = matchingRep?.lastName?.charAt(0) ?? '';

    // Show initials if either are available, fallback to a placeholder if not
    if (firstInitial || lastInitial) {
      return (
        <div className="text-[13px] text-[#D26033] flex items-center justify-center w-9 h-9 rounded-full bg-[#F6C796] uppercase font-bold">
          {`${firstInitial}${lastInitial}`}
        </div>
      );
    }

    return (
      <div className="text-[13px] text-gray-500 flex items-center justify-center w-9 h-9 rounded-full bg-gray-200" />
    );
  }, [matchingRep]);

  if (isLoadingReps) {
    return null;
  }

  return (
    <div className="w-full flex justify-between items-center">
      <div className="flex items-center gap-2">
        {repImage}

        <div className="flex flex-col gap-[2px]">
          <Text variant="Caption1" className="text-[#86868B]">
            {intl?.formatMessage(
              definedTranslation.assignedRepSelect.assignedRepLabel,
            )}
          </Text>
          <Text
            variant="Body"
            className="text-[#4D4E58] truncate max-w-[260px]"
          >
            {matchingRep?.label ??
              intl?.formatMessage(
                definedTranslation.assignedRepSelect.noAvailableReps,
              )}
          </Text>
        </div>
      </div>

      {reps?.length >= 2 && (
        <Select
          value={currentRepId}
          onChange={onRepChange}
          direction="bottom-start"
          options={reps}
        >
          <SelectTrigger className="w-full bg-transparent text-[16px] px-2 py-1">
            <Text variant="Body" className="text-[#4D4E58]">
              {intl?.formatMessage(
                definedTranslation.assignedRepSelect.changeRepLabel,
              )}
            </Text>
          </SelectTrigger>
          <SelectContent>
            {reps.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};
